import React, { useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Restaurant, RestaurantShift } from '@/services/innovorder/restaurant/restaurant.type';
import { Input } from '@/components/form/Input';
import { MultiSelect } from '@/components/form/MultiSelect';
import { Switch } from '@/components/form/Switch';
import { ColorPicker } from '@/components/form/ColorPicker';
import { SavingBar } from '@/components/atoms/SavingBar';
import { Label } from '@/components/atoms/Label';
import { computeText } from '@/locales/utils';
import { Title } from '@/components/atoms/Title';
import { Text } from '@/components/atoms/Text';
import {
    ShiftFormValues,
    buildDaysOfWeekOptions,
    daysOfWeekToOptions,
    defaultShiftFormValues,
    hhmmToMinutes,
    isHHmm,
    minutesToHHmm,
    optionsToDaysOfWeek,
} from './shiftForm.utils';
import { ConfirmationBarContainer, Container, Field, FormCard, Header, Hint, Row, TimeInput } from './ShiftForm.style';

export type ShiftFormSubmitPayload = {
    name: string;
    restaurantId: number;
    daysOfWeek: string;
    start: number;
    end: number;
    endsTomorrow: boolean;
    color: string;
    isEnabled: boolean;
    isDefault: boolean;
};

export type ShiftFormProps = {
    mode: 'create' | 'edit';
    shift?: RestaurantShift;
    restaurants: Restaurant[];
    isLoading?: boolean;
    error?: string;
    onSubmit: (payload: ShiftFormSubmitPayload) => void;
};

export const ShiftForm: React.FunctionComponent<React.PropsWithChildren<ShiftFormProps>> = ({
    mode,
    shift,
    restaurants,
    isLoading,
    error,
    onSubmit,
}) => {
    const intl = useIntl();
    const isEdit = mode === 'edit';

    const restaurantOptions = useMemo(
        () => restaurants.map((restaurant) => ({ value: restaurant.restaurantId, label: restaurant.name })),
        [restaurants],
    );
    const daysOfWeekOptions = useMemo(() => buildDaysOfWeekOptions(intl), [intl]);

    const initialValues = useMemo<ShiftFormValues>(() => {
        if (!shift) return defaultShiftFormValues;
        const restaurantOption = restaurantOptions.find((option) => option.value === shift.restaurantId);
        return {
            name: shift.name,
            restaurantId: restaurantOption ?? null,
            daysOfWeek: daysOfWeekToOptions(shift.daysOfWeek, intl),
            start: minutesToHHmm(shift.start),
            end: minutesToHHmm(shift.end),
            endsTomorrow: shift.endsTomorrow,
            color: shift.color,
            isEnabled: shift.isEnabled,
            isDefault: shift.isDefault,
        };
    }, [shift, restaurantOptions, intl]);

    const form = useForm<ShiftFormValues>({
        defaultValues: initialValues,
        values: initialValues,
        mode: 'onChange',
    });

    const { handleSubmit, reset, watch, setError, clearErrors, formState } = form;

    const startValue = watch('start');
    const endValue = watch('end');

    useEffect(() => {
        if (!isHHmm(startValue)) {
            setError('start', { type: 'format', message: computeText(intl, 'shift.error.hours.format') });
            return;
        }
        if (!isHHmm(endValue)) {
            setError('end', { type: 'format', message: computeText(intl, 'shift.error.hours.format') });
            return;
        }
        if (hhmmToMinutes(endValue) <= hhmmToMinutes(startValue)) {
            setError('end', {
                type: 'inconsistency',
                message: computeText(intl, 'shift.error.hours.inconsistency'),
            });
            return;
        }
        clearErrors(['start', 'end']);
    }, [startValue, endValue, setError, clearErrors, intl]);

    const submit = handleSubmit((values) => {
        if (!values.restaurantId) {
            setError('restaurantId', { type: 'required', message: computeText(intl, 'field.required') });
            return;
        }
        if (values.daysOfWeek.length === 0) {
            setError('daysOfWeek', {
                type: 'required',
                message: computeText(intl, 'shift.error.daysOfWeek.required'),
            });
            return;
        }
        onSubmit({
            name: values.name,
            restaurantId: values.restaurantId.value,
            daysOfWeek: optionsToDaysOfWeek(values.daysOfWeek),
            start: hhmmToMinutes(values.start),
            end: hhmmToMinutes(values.end),
            endsTomorrow: values.endsTomorrow,
            color: values.color,
            isEnabled: values.isEnabled,
            isDefault: values.isDefault,
        });
    });

    return (
        <FormProvider {...form}>
            <Container>
                <Header>
                    <Title text={isEdit ? 'shift.form.title.edit' : 'shift.form.title.create'} />
                    <Text text="shift.form.subtitle" weight="light" />
                </Header>

                <FormCard data-testid="shift-form">
                    <Row>
                        <Field>
                            <Label labelId="shift.field.name" inputId="shift-name" />
                            <Input
                                name="name"
                                placeholder={computeText(intl, 'shift.field.name.placeholder')}
                                rules={{ required: computeText(intl, 'field.required') }}
                                data-testid="shiftForm-nameInput"
                            />
                        </Field>
                        <Field>
                            <Label labelId="shift.field.restaurant" inputId="shift-restaurant" />
                            <MultiSelect
                                inputId="shift-restaurant"
                                isMulti={false}
                                name="restaurantId"
                                options={restaurantOptions}
                                placeholder={computeText(intl, 'shift.field.restaurant.placeholder')}
                                isDisabled={isEdit}
                            />
                            {isEdit && (
                                <Hint>
                                    <FormattedMessage id="shift.field.restaurant.hint.edit" />
                                </Hint>
                            )}
                        </Field>
                    </Row>

                    <Field>
                        <Label labelId="shift.field.daysOfWeek" inputId="shift.field.daysOfWeek" />
                        <MultiSelect
                            inputId="shift.field.daysOfWeek"
                            isMulti
                            name="daysOfWeek"
                            options={daysOfWeekOptions}
                            placeholder={computeText(intl, 'shift.field.daysOfWeek.placeholder')}
                        />
                    </Field>

                    <Row>
                        <Field>
                            <Label labelId="shift.field.start" inputId="shift-start" />
                            <Controller
                                name="start"
                                rules={{ required: computeText(intl, 'field.required') }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <TimeInput
                                            id="shift-start"
                                            type="time"
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                        />
                                        {fieldState.error?.message && <Hint>{fieldState.error.message}</Hint>}
                                    </>
                                )}
                            />
                        </Field>
                        <Field>
                            <Label labelId="shift.field.end" inputId="shift-end" />
                            <Controller
                                name="end"
                                rules={{ required: computeText(intl, 'field.required') }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <TimeInput
                                            id="shift-end"
                                            type="time"
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                        />
                                        {fieldState.error?.message && <Hint>{fieldState.error.message}</Hint>}
                                    </>
                                )}
                            />
                        </Field>
                        <Field>
                            <Label labelId="shift.field.endsTomorrow" inputId="shift-endsTomorrow" />
                            <Switch name="endsTomorrow" />
                        </Field>
                    </Row>

                    <Row>
                        <Field>
                            <Label labelId="shift.field.color" inputId="shift-color" />
                            <ColorPicker name="color" />
                        </Field>
                        <Field>
                            <Label labelId="shift.field.isEnabled" inputId="shift-isEnabled" />
                            <Switch name="isEnabled" />
                        </Field>
                        <Field>
                            <Label labelId="shift.field.isDefault" inputId="shift-isDefault" />
                            <Switch name="isDefault" disabled={isEdit} />
                            {isEdit && (
                                <Hint>
                                    <FormattedMessage id="shift.field.isDefault.hint" />
                                </Hint>
                            )}
                        </Field>
                    </Row>
                </FormCard>
            </Container>

            <ConfirmationBarContainer>
                <SavingBar
                    onSave={submit}
                    loading={isLoading}
                    error={error}
                    changesCount={Object.keys(formState.dirtyFields).length}
                    onCancel={() => reset(initialValues)}
                />
            </ConfirmationBarContainer>
        </FormProvider>
    );
};
